import React from "react";
import { Link } from "react-router-dom";
import {
  Stamp,
  Globe,
  ShieldCheck,
  FileCheck,
  ArrowUpRight,
  Building2,
  Plane,
  ScrollText,
} from "lucide-react";
import {
  PageHero,
  GoldButton,
  GhostGoldButton,
  SectionEyebrow,
  FadedBackdrop,
} from "@/components/ui-bits";
import { IMG } from "@/constants/images";

const APOSTILLE_TYPES = [
  {
    icon: ScrollText,
    title: "Personal Documents",
    body: "Birth, marriage, death certificates · diplomas, transcripts · single-status & adoption documentation prepared for international use.",
  },
  {
    icon: Building2,
    title: "Corporate Documents",
    body: "Articles of Incorporation, Certificates of Good Standing, Powers of Attorney, Bylaws, and international entity-formation packets.",
  },
  {
    icon: Plane,
    title: "International Use",
    body: "Hague Convention apostilles for the 120+ countries that recognize them, plus chain authentication for non-Hague destinations.",
  },
  {
    icon: FileCheck,
    title: "Legal & Estate",
    body: "Court orders, sworn statements, affidavits, estate documents — all authenticated for cross-border legal effect.",
  },
];

const PROCESS = [
  {
    step: "01",
    title: "Document Audit",
    body: "Send us your document(s). We confirm the destination country, identify whether an apostille or chain authentication is required, and verify the document is properly notarized or certified for processing.",
  },
  {
    step: "02",
    title: "Florida Secretary of State Submission",
    body: "We hand-walk your documents to the Florida Secretary of State Apostille Division. The State has standardized to a mandatory one-business-day processing window (same-day in-person submission is no longer offered). Our personally walked-in service compresses the full turnaround to as little as 2–3 business days (Rush) or 4–7 business days (Expedited) — versus the ~3 weeks that mail-in apostilles typically take.",
  },
  {
    step: "03",
    title: "Authenticated & Returned",
    body: "Once apostilled, we courier the documents back to you or directly to your intended international recipient — with tracking, secure handling, and full chain of custody.",
  },
];

export default function ApostillePage() {
  return (
    <>
      <PageHero
        eyebrow="Premiere Apostille Agent · National & International"
        title="Florida Secretary of State Apostilles, handled end-to-end."
        lead="Built on 45 years of federal-grade international document experience and thousands of multi-national legal packages, our Apostille service authenticates your U.S. documents for use in 120+ Hague Convention countries — and provides chain authentication for non-Hague destinations."
        backdrop={IMG.apostilleStateHero}
        backdropOpacity={0.55}
        bright
      >
        <div className="flex flex-wrap gap-4">
          <GoldButton as={Link} to="/contact?service=Apostille" data-testid="apostille-book-btn">
            Request an Apostille Quote <ArrowUpRight className="w-4 h-4" />
          </GoldButton>
          <GhostGoldButton
            as={Link}
            to="/vendor"
            data-testid="apostille-vendor-btn"
          >
            For Law Firms & Corporates
          </GhostGoldButton>
        </div>
      </PageHero>

      {/* Why us */}
      <section className="border-b border-white/10 bg-[#1A2B45]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2 flex lg:justify-center">
            <div className="w-16 h-16 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
              <Globe className="w-7 h-7" />
            </div>
          </div>
          <div className="lg:col-span-10">
            <SectionEyebrow className="mb-3">Why Use Our Apostille Service</SectionEyebrow>
            <p className="font-serif-display text-2xl sm:text-3xl text-white leading-snug">
              "We have personally walked thousands of multi-national document
              packages through Secretary of State, Department of State, and
              foreign embassy chains — your apostille is not our first."
            </p>
          </div>
        </div>
      </section>

      {/* Document Types */}
      <section className="relative overflow-hidden py-24 sm:py-32 border-b border-white/10">
        <FadedBackdrop src={IMG.apostilleEmbassyBackdrop} opacity={0.2} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Documents We Apostille</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Personal, corporate, legal — handled with the same federal-grade discipline.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {APOSTILLE_TYPES.map((t, i) => (
              <div
                key={i}
                data-testid={`apostille-type-${i}`}
                className="border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-6">
                  <t.icon className="w-6 h-6" />
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-3">
                  {t.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-step process */}
      <section className="py-24 sm:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">Our Process</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Three steps from your document to international acceptance.
            </h2>
          </div>
          <ol className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {PROCESS.map((s, i) => (
              <li
                key={i}
                data-testid={`apostille-step-${i + 1}`}
                className="relative border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                    <Stamp className="w-6 h-6" />
                  </div>
                  <span className="font-serif-display text-4xl text-[#D4AF37]/40">
                    {s.step}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-3">
                  {s.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Disclaimer / CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <SectionEyebrow className="mb-3">Pricing & Timing</SectionEyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white tracking-tight leading-tight">
              Flat-rate apostille processing, quoted by document set and destination.
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed text-sm">
              Pricing depends on the number of documents, the destination
              country (Hague vs non-Hague), and your turnaround tier:{" "}
              <span className="text-white font-semibold">Rush (2–3 business days)</span>,{" "}
              <span className="text-white font-semibold">Expedited (4–7 business days)</span>,
              or <span className="text-white">Standard mail-in (~3 weeks)</span>.
              All Rush and Expedited tiers are based on personally walking
              your documents into the Florida Secretary of State — leveraging
              the State's mandatory one-business-day in-person processing
              window. Quotes include authentication, secure courier delivery,
              and full tracking.
            </p>
            <p className="mt-4 text-slate-500 text-xs leading-relaxed">
              Apostille services are a professional document-handling and
              authentication service — not legal advice. We do not draft or
              interpret documents. For legal counsel, consult a licensed
              attorney.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-8">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
                <div className="eyebrow">Authorized Premiere Apostille Agent</div>
              </div>
              <p className="font-serif-display text-2xl text-white leading-snug mb-6">
                Talk to a real human about your specific destination country
                and timing.
              </p>
              <GoldButton
                as={Link}
                to="/contact?service=Apostille"
                className="w-full"
                data-testid="apostille-contact-btn"
              >
                Request a Quote <ArrowUpRight className="w-4 h-4" />
              </GoldButton>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
